<template>
  <Loader v-if="loading"/>
  <v-container v-else class="weather-container py-3 px-5">
    <v-row  v-for="(day, index) in forecastForWeek" :key="index" class="weather-row" align="center" :class="{'no-border': index === forecastForWeek.length - 1}">
      <v-col cols="2" class="day-label d-flex align-center">
        <span v-if="index === 0">Today</span>
        <span v-else>{{ day.date | localizedDate("ddd") }}</span>
      </v-col>
      
      <v-col :cols="is5K ? 6 : is4K ? 5 : 5" class="short-phrase-label d-flex align-center">
        {{ is4K || is5K ? day.day.longPhrase : day.day.shortPhrase }}
      </v-col>

      <v-col cols="2" class="weather-icon d-flex justify-start">
        <v-icon :size="is4K || is5K ? 75 : 37" color="white">{{ iconMap[day.day.iconCode] || "mdi-help-circle" }}</v-icon>
      </v-col>

      <v-col :cols="is5K ? 2 : is4K ? 3 : 3" class="unit-label d-flex justify-start pl-0">
        {{ day.temperature.minimum.value }} {{unit}} &nbsp; {{ day.temperature.maximum.value }} {{unit}}
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Loader from "@/components/loader/Loader.vue";
export default {
  name: 'Weather',
  components: {
    Loader
  },
  computed: {
    facility(){
      return this.$store.state.facilityModule.selectedFacility
    },
    allForecasts() {
      return this.$store.state.facilityModule.currentWeatherData?.forecasts
    },
    forecastForWeek() {
      return this.allForecasts?.slice(0, 7).map(day => ({
        ...day,
        temperature: {
          minimum: {
            value: this.roundUp(day.temperature.minimum.value),
            unit: day.temperature.minimum.unit
          },
          maximum: {
            value: this.roundUp(day.temperature.maximum.value),
            unit: day.temperature.minimum.unit
          }
        }
      }));
    },
    unit() {
      return this.forecastForWeek[0]?.temperature.maximum.unit === 'C' ? '°C' : 'F';
    },
  },
  data() {
    return {
      loading: true,
      is4K: window.innerWidth >= 3840 && window.innerWidth < 5120,
      is5K: window.innerWidth >= 5120,
      iconMap: {
        1: "mdi-weather-sunny",
        2: "mdi-weather-sunny",
        3: "mdi-weather-partly-cloudy",
        4: "mdi-weather-partly-cloudy",
        5: "mdi-weather-hazy",
        6: "mdi-weather-cloudy",
        7: "mdi-weather-cloudy",
        8: "mdi-weather-cloudy",
        11: "mdi-weather-fog",
        12: "mdi-weather-rainy",
        13: "mdi-weather-partly-rainy",
        14: "mdi-weather-partly-rainy",
        15: "mdi-weather-lightning-rainy",
        16: "mdi-weather-lightning",
        17: "mdi-weather-partly-lightning",
        18: "mdi-weather-pouring",
        19: "mdi-weather-snowy-rainy",
        20: "mdi-weather-snowy",
        21: "mdi-weather-snowy",
        22: "mdi-weather-snowy-heavy",
        23: "mdi-weather-snowy",
        24: "mdi-weather-hail",
        25: "mdi-weather-snowy-rainy",
        26: "mdi-weather-snowy-rainy",
        29: "mdi-weather-snowy-rainy",
        30: "mdi-weather-sunny-alert",
        31: "mdi-weather-snowy-heavy",
        32: "mdi-weather-windy",
        33: "mdi-weather-night",
        34: "mdi-weather-night-partly-cloudy",
        35: "mdi-weather-night-partly-cloudy",
        36: "mdi-weather-night-partly-cloudy",
        37: "mdi-weather-hazy",
        38: "mdi-weather-cloudy",
        39: "mdi-weather-partly-rainy",
        40: "mdi-weather-rainy",
        41: "mdi-weather-partly-lightning",
        42: "mdi-weather-lightning-rainy",
        43: "mdi-weather-snowy",
        44: "mdi-weather-snowy-heavy",
      }
    };
  },
  methods: {
    updateScreenSize() {
      this.is4K = window.innerWidth >= 3840 && window.innerWidth < 5120;
      this.is5K = window.innerWidth >= 5120;
      this.$forceUpdate();
    },
    async fetchWeatherData() {
      await this.$store.dispatch("getWeatherData");
    },
    scheduleDailyUpdate() {
      if (this.dailyUpdateInterval) {
        clearInterval(this.dailyUpdateInterval);
      }
      if (this.midnightTimeout) {
        clearTimeout(this.midnightTimeout);
      }

      const now = new Date();
      const nextMidnight = new Date();
      nextMidnight.setHours(0, 0, 0, 0);
      nextMidnight.setDate(nextMidnight.getDate() + 1); // The next day at midnight

      const timeUntilMidnight = nextMidnight.getTime() - now.getTime();

      this.midnightTimeout = setTimeout(() => {
        this.fetchWeatherData();
        this.dailyUpdateInterval = setInterval(this.fetchWeatherData, 12 * 60 * 60 * 1000); // Updates every 12 hours
      }, timeUntilMidnight);
    },
    roundUp(value) {
      return Math.ceil(value);
    }
  },
  watch: {
    async 'facility.countryIso'(newValue) {
      //get data with new unit
      console.log('new countryIso: ', newValue);
      await this.$store.dispatch("getWeatherData");
    }
  },
  async created() {
    try {
      await this.fetchWeatherData();
      this.scheduleDailyUpdate();
    } finally {
      this.loading = false
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateScreenSize);
  },
  beforeDestroy() {
    if (this.dailyUpdateInterval) {
      clearInterval(this.dailyUpdateInterval);
      this.dailyUpdateInterval = null;
    }
    if (this.midnightTimeout) {
      clearTimeout(this.midnightTimeout);
      this.midnightTimeout = null;
    }
    window.removeEventListener('resize', this.updateScreenSize);
  }
};
</script>

<style scoped>
.weather-container {
  background-color: #bdbebf24;
  padding: 0 15px 0 20px;
  color: white;
}

.weather-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 85px;
}

.weather-row.no-border {
  border-bottom: none;
}

.day-label {
  font-weight: bolder;
  text-transform: uppercase;
}

.short-phrase-label {
  color: whitesmoke;
}

.weather-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.unit-label {
  color: whitesmoke;
  text-align: right;
}

/* Full HD < 4K */
@media screen and (min-width: 1921px) and (max-width: 3839px) {
  .day-label {
    font-size: 18px;
    padding-left: 10px;
  }
  .short-phrase-label {
    font-size: 15px;
  }
  .unit-label {
    font-size: 21px;
    padding-right: 15px;
  }
  .weather-row {
    padding: 10px 0;
  }
}

/* 4K (3840px – 5119px) */
@media screen and (min-width: 3840px) and (max-width: 5119px) {
  .day-label {
    font-size: 32px;
    padding-left: 30px;
  }
  .short-phrase-label {
    font-size: 25px;
  }
  .unit-label {
    font-size: 32px;
    padding-right: 35px;
  }
  .weather-row {
    padding: 30px 0;
  }
}

/* 5K+ (5120px and more) */
@media screen and (min-width: 5120px) {
  .day-label {
    font-size: 37px;
    padding-left: 30px;
  }
  .short-phrase-label {
    font-size: 30px;
  }
  .unit-label {
    font-size: 33px;
    padding-right: 30px;
  }
  .weather-row {
    padding: 40px 0;
  }
}
</style>
